import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Languages } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Money, Th, Td, Stat, Select } from "../components/common";
import { useI18n } from "../lib/i18n";

export default function PublicSummary() {
  const { toggle, nextLabel } = useI18n();
  const [year, setYear] = useState(new Date().getFullYear());
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/public/summary", { params: { year } }).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, [year]);
  return (
    <div className="min-h-screen bg-[#FAF7F2] grain">
      <header className="bg-[#5C4033] text-white">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div>
            <div className="font-serif font-bold text-lg">St. Francis of Assisi Parish</div>
            <div className="text-xs text-white/75">Brgy. Mabuhay, General Santos City · Diocese of Marbel</div>
          </div>
          <div className="flex gap-2">
            <button onClick={toggle} data-testid="public-language-toggle" className="h-11 rounded-full bg-[#D4AF37] text-[#2F2A24] px-3 font-bold text-sm inline-flex items-center gap-1"><Languages className="h-4 w-4" />{nextLabel}</button>
            <Link to="/login" data-testid="public-login-link" className="h-11 rounded-full bg-white/10 px-4 font-bold text-sm inline-flex items-center">Sign in</Link>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="font-serif text-3xl sm:text-4xl font-bold fade-up">Public Financial Transparency Summary</h1>
        <p className="text-[#6E6259] mt-2 max-w-3xl">How community funds were received, spent and remitted by each GKK. Only verified, official totals are shown here — no names or personal details. Members may sign in for detailed reports.</p>
        <div className="mt-4 max-w-[180px]">
          <Select data-testid="public-year-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[0, 1, 2].map((i) => { const y = new Date().getFullYear() - i; return <option key={y} value={y}>{y}</option>; })}
          </Select>
        </div>
        {err && <p className="mt-6 rounded-xl bg-[#FEF2F2] p-4 text-[#B91C1C]">{err}</p>}
        {d && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6 stagger">
              <Stat label="Collected (verified)" c={d.parish_total.gross_collections_cents} testid="public-total-collections" />
              <Stat label="Spent" c={d.parish_total.expenses_paid_cents} />
              <Stat label="Remitted" c={d.parish_total.remittances_cents} />
              <Stat label="Remaining (cash & bank)" c={d.parish_total.total_balance_cents} />
            </div>
            <div className="tbl-wrap mt-6">
              <table className="tbl" data-testid="public-gkk-table">
                <thead><tr><Th>GKK</Th><Th r>Regular</Th><Th r>Special</Th><Th r>Expenses</Th><Th r>Remitted</Th><Th r>Balance</Th><Th>Last entry</Th></tr></thead>
                <tbody>
                  {d.gkks.map((g) => (
                    <tr key={g.gkk_id}>
                      <Td><div className="font-semibold">{g.gkk_name}</div><div className="text-xs text-[#6E6259]">{g.zone}</div></Td>
                      {g.reported ? (
                        <>
                          <Td r><Money c={g.regular_cents + g.other_cents} /></Td><Td r><Money c={g.special_cents} /></Td><Td r><Money c={g.expenses_paid_cents} /></Td>
                          <Td r><Money c={g.remittances_cents} /></Td><Td r><Money c={g.total_balance_cents} className="font-bold" /></Td><Td>{g.last_entry_date}</Td>
                        </>
                      ) : <Td className="text-[#B91C1C] font-semibold" ><span>Not yet reported</span></Td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-[#6E6259] mt-3">{d.note} Period: {d.period}. Last updated {d.last_updated?.replace("T", " ").slice(0, 16)} PST. Collections differ across GKKs because of community size and circumstances; totals are not a measure of faithfulness.</p>
          </>
        )}
      </main>
    </div>
  );
}
